import { Component, computed, effect, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  L10N_LOCALE,
  L10nTranslatePipe,
  L10nTranslationService,
} from 'angular-l10n';
import { SvgIconComponent } from 'angular-svg-icon';
import { BasicUserDetails } from '@app/account/models/basic-user-details';
import { PaymentStatus } from '@app/shared/models/current-user';
import { getActiveVacationSelector } from '@app/shared/reducers/user.selectors';
import { Store } from '@ngrx/store';
import { VacationService } from '@app/account/services/vacation.service';
import { ToastType } from '@app/shared/models/toast.model';
import { NotificationService } from '@app/shared/services';
import { CountdownPipe } from '@app/shared/pipes/countdown.pipe';
import { VacationTimesPipe } from '@app/shared/pipes/vacation-times.pipe';
import { getActiveVacationAction } from '@app/shared/actions/shared.actions';
import * as dayjs from 'dayjs';

@Component({
  selector: 'live-account-banner',
  standalone: true,
  imports: [
    CommonModule,
    L10nTranslatePipe,
    RouterLink,
    SvgIconComponent,
    CountdownPipe,
    VacationTimesPipe,
  ],
  templateUrl: './live-account-banner.component.html',
  styleUrls: ['./live-account-banner.component.scss'],
})
export class LiveAccountBanner {
  readonly locale = inject(L10N_LOCALE);
  @Input() isClient: boolean;
  @Input() creditBalance: number;
  @Input() optionalProgress: number;
  @Input() browseUserDetails: BasicUserDetails;
  @Input() paymentStatus: PaymentStatus = PaymentStatus.Active;
  paymentStatusEnum = PaymentStatus;
  private readonly vacationService = inject(VacationService);
  private readonly notifyService = inject(NotificationService);
  readonly translation = inject(L10nTranslationService);
  private readonly store = inject(Store);
  readonly activeVacation = this.store.selectSignal(getActiveVacationSelector);
  readonly profileInVacationVisible = computed(
    () => dayjs().add(7, 'day') >= dayjs(this.activeVacation().endDate)
  );

  alertLimit = -800;
  inactiveLimit = -1000;
  inProgress = false;
  vacationBannerDate: string;
  showProfileDaysAgo = -7;

  constructor() {
    effect(() => {
      if (this.activeVacation()) {
        this.vacationBannerDate = dayjs(this.activeVacation().endDate)
          .add(1, 'minute')
          .format('DD/MM/YYYY');
        if (dayjs().add(7, 'day') < dayjs(this.activeVacation().endDate)) {
          this.vacationBannerDate = dayjs(this.activeVacation().endDate)
            .add(this.showProfileDaysAgo + 1, 'day')
            .format('DD/MM/YYYY');
        }
      }
    });
  }

  endVacation() {
    if (this.inProgress) {
      return;
    }

    this.inProgress = true;
    this.vacationService
      .editVacation({
        ...this.activeVacation(),
        endDate: new Date(),
      })
      .subscribe((result) => {
        this.inProgress = false;
        if (result) {
          this.notifyService.addToast({
            message: this.translation.translate(
              'ACCOUNT.SETTINGS.VACATION.Vacation ended successfully'
            ),
            type: ToastType.Success,
            timeout: 5000,
          });
          this.store.dispatch(getActiveVacationAction());
        } else {
          this.notifyService.addToast({
            message: this.translation.translate(
              'ACCOUNT.Some server error! Try again later'
            ),
            type: ToastType.Error,
            timeout: 5000,
          });
        }
      });
  }
}
